import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { TrendingUp, ShoppingBag, Trophy, RefreshCw } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";
import { formatEuro } from "../lib/menu";

const DISH_COLORS = ["#1B6B2E", "#B71C1C", "#F4D03F", "#0D3B4C", "#2E8B57", "#E67E22", "#8E44AD", "#16A085"];

const RevenueTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border-2 border-[#0D3B4C] px-3 py-2 text-sm font-[600] text-[#0D3B4C]" data-testid="sales-tooltip">
      <div className="font-[700]">{p.label}</div>
      <div>Omzet: {formatEuro(p.revenue)}</div>
      <div>{p.orders} bestellingen · {p.portions} porties</div>
    </div>
  );
};

const PERIODS = [
  { key: "week", label: "Deze week" },
  { key: "month", label: "Deze maand" },
  { key: "year", label: "Dit jaar" },
  { key: "all", label: "Alles" },
];
const CHART_TITLE = { day: "Omzet per dag", week: "Omzet per week", month: "Omzet per maand" };

export const SalesOverview = ({ onAuthError }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sales-overview`, { params: { period }, headers: authHeader() });
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthError?.();
      else toast.error("Verkoopoverzicht laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [authHeader, onAuthError, period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 text-center font-[600] text-[#0D3B4C]" data-testid="sales-loading">Bezig met laden...</div>;
  if (!data) return null;

  const buckets = data.buckets || [];
  const topDishes = data.top_dishes || [];
  const maxPortions = Math.max(1, ...topDishes.map((d) => d.portions));
  const noData = buckets.length === 0 && topDishes.length === 0;
  const chartTitle = CHART_TITLE[data.granularity] || "Omzet";

  return (
    <div className="space-y-6" data-testid="sales-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-[700] text-[#0D3B4C]">Verkoopoverzicht</h2>
        <button onClick={load} data-testid="sales-refresh" className="flex items-center gap-2 bg-[#E8F5E9] text-[#0D3B4C] font-[700] px-4 py-2 hover:bg-[#d4ecd7] transition-colors">
          <RefreshCw size={16} /> Vernieuwen
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="sales-period-filter">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`period-${p.key}`}
            className={`px-4 py-2 font-[700] text-sm border-2 transition-colors ${period === p.key ? "bg-[#1B6B2E] text-white border-[#1B6B2E]" : "bg-white text-[#0D3B4C] border-[#E8F5E9] hover:border-[#1B6B2E]"}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {noData ? (
        <div className="bg-[#FEF9E7] border-2 border-[#F4D03F] p-8 text-center font-[600] text-[#0D3B4C]" data-testid="sales-empty">
          {period === "all" ? "Nog geen betaalde bestellingen om weer te geven." : "Geen betaalde bestellingen in deze periode."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#1B6B2E] text-white p-5" data-testid="sales-total-revenue">
              <div className="flex items-center gap-2 text-sm font-[600] opacity-90"><TrendingUp size={18} /> Totale omzet</div>
              <div className="text-3xl font-[700] mt-2">{formatEuro(data.totals?.revenue || 0)}</div>
            </div>
            <div className="bg-[#0D3B4C] text-white p-5" data-testid="sales-total-orders">
              <div className="flex items-center gap-2 text-sm font-[600] opacity-90"><ShoppingBag size={18} /> Betaalde bestellingen</div>
              <div className="text-3xl font-[700] mt-2">{data.totals?.orders || 0}</div>
            </div>
            <div className="bg-[#B71C1C] text-white p-5" data-testid="sales-top-dish">
              <div className="flex items-center gap-2 text-sm font-[600] opacity-90"><Trophy size={18} /> Populairste gerecht</div>
              <div className="text-xl font-[700] mt-2 leading-tight">{topDishes[0]?.name || "—"}</div>
              {topDishes[0] && <div className="text-sm font-[600] opacity-90">{topDishes[0].portions} porties</div>}
            </div>
          </div>

          <div className="bg-white border-2 border-[#E8F5E9] p-5">
            <h3 className="font-[700] text-[#0D3B4C] mb-4">{chartTitle}</h3>
            <div style={{ width: "100%", height: 300 }} data-testid="sales-revenue-chart">
              <ResponsiveContainer>
                <BarChart data={buckets} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8F5E9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#0D3B4C", fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: "#0D3B4C" }} tickLine={false} />
                  <YAxis tick={{ fill: "#0D3B4C", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${v}`} width={60} />
                  <Tooltip content={<RevenueTooltip />} cursor={{ fill: "rgba(27,107,46,0.08)" }} />
                  <Bar dataKey="revenue" fill="#1B6B2E" radius={[4, 4, 0, 0]} maxBarSize={56} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border-2 border-[#E8F5E9] p-5">
            <h3 className="font-[700] text-[#0D3B4C] mb-4">Populairste gerechten</h3>
            <div className="space-y-3" data-testid="sales-top-dishes">
              {topDishes.map((d, i) => (
                <div key={d.name} className="flex items-center gap-3" data-testid={`top-dish-${i}`}>
                  <div className="w-6 text-right font-[700] text-[#0D3B4C]">{i + 1}</div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="font-[700] text-[#0D3B4C]">{d.name}</span>
                      <span className="font-[600] text-[#0D3B4C]">{d.portions} porties · {formatEuro(d.revenue)}</span>
                    </div>
                    <div className="h-3 bg-[#E8F5E9] overflow-hidden">
                      <div className="h-full" style={{ width: `${(d.portions / maxPortions) * 100}%`, backgroundColor: DISH_COLORS[i % DISH_COLORS.length] }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
